import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ShieldAlert, UserCheck, Store, Sparkles, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const Hero = ({ t, isDark, lang }) => {
  const platforms = [
    {
      name: "Kanan",
      tag: lang === 'es' ? "Seguridad Cívica" : "Civic Safety",
      path: "/proyectos/kanan",
      icon: ShieldAlert,
      badge: "Live"
    },
    {
      name: "Blebe",
      tag: lang === 'es' ? "Selección & Tiempos" : "Hiring Vetting",
      path: "/proyectos/Blebe",
      icon: UserCheck,
      badge: "Beta"
    },
    {
      name: "Marchantite",
      tag: lang === 'es' ? "Comercio de Barrio" : "Local Trade",
      path: "/proyectos/Marchantite",
      icon: Store,
      badge: "Local"
    },
   {
      name: "Niya",
      tag: lang === 'es' ? "Salud Financiera" : "Financial Clarity",
      path: "/proyectos/Niya",
      icon: TrendingUp,
      badge: "Finanzas"
    }
  ];

  return (
    <section
      id="home"
      className={`relative min-h-[85vh] flex items-center justify-center px-6 pt-28 pb-16 transition-colors duration-500 overflow-hidden ${
        isDark ? 'bg-[#0A0F1A] text-white' : 'bg-[#F8FAFC] text-slate-900'
      }`}
    >
      {/* Fondo estático ultraligero */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `radial-gradient(currentColor 1px, transparent 1px)`,
          backgroundSize: '28px 28px'
        }}
      />
      <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-[120px] pointer-events-none opacity-20 ${
        isDark ? 'bg-blue-500' : 'bg-blue-300'
      }`} />

      <div className="container mx-auto max-w-5xl relative z-10 text-center">
        
        {/* Badge superior */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 mb-8"
        >
          <Sparkles size={12} className="text-blue-400" />
          <span className="text-[10px] font-black uppercase tracking-[0.25em] text-blue-400">
            {lang === 'es' ? 'ECOSISTEMA DE SOFTWARE INDEPENDIENTE' : 'INDEPENDENT SOFTWARE ECOSYSTEM'}
          </span>
        </motion.div>

        {/* Titular contundente */}
        <motion.h1 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="text-4xl sm:text-6xl md:text-7xl font-black tracking-tighter leading-tight mb-6"
        >
          {lang === 'es' ? 'Tecnología móvil que' : 'Mobile software built to'}{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-400">
            {lang === 'es' ? 'resuelve el día a día.' : 'solve everyday life.'}
          </span>
        </motion.h1>

        {/* Subtítulo breve */}
        <motion.p 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-base sm:text-lg md:text-xl font-bold opacity-75 max-w-2xl mx-auto mb-16 leading-relaxed"
        >
          {lang === 'es'
            ? 'Creamos aplicaciones pensadas para cuidar tu entorno, agilizar decisiones de trabajo y respaldar al comercio local sin intermediarios abusivos.'
            : 'We craft dedicated platforms to protect local communities, streamline hiring, and empower neighborhood shops.'}
        </motion.p>

        {/* Grid minimalista de acceso directo a cada app */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {platforms.map((item, idx) => {
            const Icon = item.icon;
            return (
              <Link
                key={idx}
                to={item.path}
                className={`p-5 rounded-2xl border text-left transition-all group flex flex-col justify-between min-h-[110px] ${
                  isDark 
                    ? 'bg-slate-900/30 border-white/5 hover:border-blue-500/40 hover:bg-slate-900/60' 
                    : 'bg-white border-slate-200 hover:border-blue-500/40 shadow-sm hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between">
                  <Icon size={18} className="text-blue-500 group-hover:scale-110 transition-transform" />
                  <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                    {item.badge}
                  </span>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-black tracking-tight group-hover:text-blue-500 transition-colors">
                      {item.name}
                    </h3>
                    <ArrowUpRight size={14} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                  </div>
                  <p className="text-[10px] font-bold opacity-50 uppercase tracking-wider">{item.tag}</p>
                </div>
              </Link>
            );
          })}
        </motion.div>

      </div>
    </section>
  );
};

export default Hero;